/**
 * UpsellCard — near-1:1 port of
 * `system-web/components/checkout/upsell-card.js`
 * (Figma "Upgrade Modal | Empty", node 1897:22928).
 *
 * Visual contract is the source of truth; only the data shape differs
 * (portal `UpsellQuoteItem` carries `*_cents` integers; system-web's CP
 * shape carries dollar floats). All field mappings are listed in the
 * `system-web → portal` row comments below.
 *
 * Layout, top to bottom:
 *   1. Hero image (200px) with overlaid dose pill near the bottom
 *   2. Product name (mono uppercase) + arrow-bulleted description
 *   3. Translucent value-props panel (max 4)
 *   4. Monthly / Quarterly toggle (hidden when only one frequency configured)
 *   5. White "Add to Stack | $price ▶" CTA
 *   6. "$X /mo after first month" + steel "% off your first month" pill
 *
 * NOTE on dose pill copy: system-web hardcodes "Monthly Supply (X)".
 * The portal switches to "Quarterly Supply (X)" when the toggle is
 * Quarterly — explicit product decision, intentional divergence.
 */

import type { UpsellQuoteItem } from "@/lib/api/upsellHooks";
import { memberPriceCents, useMembership } from "@/lib/membership";

export type Frequency = "monthly" | "quarterly";

function formatPrice(cents: number | null | undefined): string | null {
  if (cents == null) return null;
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

// Inlined Arrow + Checkmark icons (system-web ports). Same paths as the
// brand `<Icon name="Arrow"/>` + `<Icon name="Checkmark"/>` used in
// system-web's UpsellCard.

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 10 12"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path d="M9.75 5.62891L4.23492e-07 11.2581L9.15609e-07 -0.000259349L9.75 5.62891Z" />
    </svg>
  );
}

function CheckmarkIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className={className}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        d="M12.1 52.1l24.4 24.4 53-53"
      />
    </svg>
  );
}

export function UpsellCard({
  item,
  selected,
  frequency,
  onToggle,
  onFrequencyChange,
  kind = "treatment",
  actionLabel,
}: {
  item: UpsellQuoteItem;
  selected: boolean;
  frequency: Frequency;
  onToggle: () => void;
  onFrequencyChange: (f: Frequency) => void;
  kind?: "treatment" | "lab";
  actionLabel?: string;
}) {
  // system-web → portal field mappings:
  //   product.productID            → !!item.monthly_price_cents
  //   product.quarterlyProductID   → !!item.quarterly_price_cents
  //   product.pricingMode          → item.pricing_mode
  //   product.price                → item.monthly_price_cents
  //   product.quarterlyPrice       → item.quarterly_price_cents
  //   product.comparePrice         → item.monthly_compare_price_cents
  //   product.quarterlyComparePrice→ item.quarterly_compare_price_cents
  //   product.renewalPrice         → item.monthly_renewal_price_cents
  //   product.quarterlyRenewalPrice→ item.quarterly_renewal_price_cents
  //   product.intakeValueProps     → item.value_props
  //   product.title                → item.title
  //   product.quantity             → item.quantity
  //   product.method               → item.method
  //   product.treatmentSupport     → item.treatment_support
  //   product.descriptionPlain     → item.description
  //   product.productHero.content  → item.hero_image_url
  const hasMonthly = item.monthly_price_cents != null;
  const hasQuarterly = item.quarterly_price_cents != null;
  const showFrequencyToggle = hasMonthly && hasQuarterly;
  const isQuarterly = frequency === "quarterly" && hasQuarterly;
  const isIntro = item.pricing_mode === "intro";

  const flatCents = isQuarterly
    ? item.quarterly_price_cents
    : item.monthly_price_cents;
  const compareCents = isQuarterly
    ? item.quarterly_compare_price_cents
    : item.monthly_compare_price_cents;
  const renewalCents = isQuarterly
    ? item.quarterly_renewal_price_cents
    : item.monthly_renewal_price_cents;

  // System Members pay 10% less on treatments, first charge and renewals
  // alike. Blood tests are never member-discounted.
  const { percent: membershipPercent } = useMembership();
  const memberPercent = kind === "lab" ? 0 : membershipPercent;
  const memberFlatCents =
    flatCents != null ? memberPriceCents(flatCents, memberPercent) : null;
  const memberRenewalCents =
    renewalCents != null ? memberPriceCents(renewalCents, memberPercent) : null;

  const periodSuffix = isQuarterly ? "/ first quarter" : "/ first mo";
  const renewalPeriod = isQuarterly ? "/ quarter" : "/ mo";

  // Compute a discount % so the steel pill has truthful copy. Prefer
  // renewal vs flat (intro mode), falling back to compare vs flat.
  let discountPct: number | null = null;
  if (isIntro && renewalCents && flatCents && renewalCents > flatCents) {
    discountPct = Math.round(((renewalCents - flatCents) / renewalCents) * 100);
  } else if (compareCents && flatCents && compareCents > flatCents) {
    discountPct = Math.round(((compareCents - flatCents) / compareCents) * 100);
  }

  const valueProps = (item.value_props || []).slice(0, 4);
  // Dose pill content. system-web reads `product.quantity` first,
  // falls back to `product.method`. NEVER use the subtitle — that's
  // marketing copy.
  const doseLabel = item.quantity || item.method || "";
  // Intentional divergence from system-web: portal flips the supply
  // word based on the active frequency toggle.
  const supplyLabel = isQuarterly ? "Quarterly Supply" : "Monthly Supply";
  // Description — system-web uses `descriptionPlain` (server-flattened
  // portable text) → `treatmentSupport` fallback. Portal: same chain.
  const description = item.description || item.treatment_support || "";

  return (
    <div className="bg-black flex flex-col w-full">
      {/* Hero image — two layers per system-web pattern:
          1. fill blurred backplate so weird aspect ratios feather.
          2. intrinsic overlay translated up out of the 200px frame with
             a left/right feather mask via .upsell-card-image-crop
             (mirrors _upsell.css `.card-image-crop`). */}
      <div className="relative w-full overflow-hidden">
        <div className="relative h-[50vw] md:h-[19rem] w-full bg-[#dee3eb]">
          {item.hero_image_url && kind === "lab" ? (
            <img
              src={item.hero_image_url}
              alt="At-home blood collection device"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain p-20 pb-45"
            />
          ) : item.hero_image_url ? (
            <>
              <img
                src={item.hero_image_url}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[3rem] md:-translate-y-[4.5rem] w-[52vw] md:w-[22rem] upsell-card-image-crop flex items-center justify-center">
                <img
                  src={item.hero_image_url}
                  alt={item.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </>
          ) : null}
          {doseLabel || kind === "lab" ? (
            <div className="absolute left-[5px] right-[5px] bottom-[8px] backdrop-blur-[30px] bg-black/50 flex items-center justify-between h-[28px]">
              <div className="px-12 min-w-0">
                <div className="font-body text-12 text-[#dbdbdb] leading-none truncate m-0">
                  {kind === "lab" ? "One at-home collection kit" : `${supplyLabel} (${doseLabel})`}
                </div>
              </div>
              <div className="px-10 shrink-0">
                <div className="font-mono text-9 text-[#dbdbdb] uppercase leading-none m-0">
                  {kind === "lab" ? "Monthly subscription" : "Recommended Dose"}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Name + description (left-aligned per design) */}
      <div className="flex flex-col gap-5 px-15 pt-12 pb-12">
        <div className="font-mono text-20 text-white uppercase leading-[1.05] tracking-[-0.4px] m-0">
          {item.title}
        </div>
        {description ? (
          <div className="indent-15 text-14 md:text-16 text-[#CCCCCC] md:mt-10 relative">
            <div className="absolute top-5 left-0 text-[#656565]">
              <ArrowIcon className="w-[0.8rem] h-auto" />
            </div>
            {description}
          </div>
        ) : null}
      </div>

      {/* Value props panel */}
      {valueProps.length > 0 ? (
        <div className="backdrop-blur-[1.84px] bg-white/[0.11] px-15 py-12 w-full">
          <ul className="flex flex-col gap-8 items-start m-0 p-0">
            {valueProps.map((vp, i) => (
              <li key={i} className="flex gap-8 items-center">
                <div className="w-[1.2rem] h-[1.2rem] flex items-center justify-center rounded-full overflow-hidden shrink-0">
                  <img
                    src="/icons/icon-check.png"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="font-mono text-11 text-[#c5c2c5] uppercase leading-normal tracking-[-0.22px]">
                  {vp}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Frequency toggle + Add CTA */}
      <div className="p-15 flex flex-col gap-15">
        {showFrequencyToggle ? (
          <div className="w-full">
            <div className="bg-black border border-[#303030] flex items-center justify-between p-[3px] w-full">
              <button
                type="button"
                onClick={() => onFrequencyChange("monthly")}
                className={`flex-1 text-15 text-center font-body py-10 ${
                  !isQuarterly
                    ? "bg-[#3d3d3d] text-white"
                    : "text-[#a7a7a7] hover:text-white"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => onFrequencyChange("quarterly")}
                className={`flex-1 text-15 text-center font-body py-10 ${
                  isQuarterly
                    ? "bg-[#3d3d3d] text-white"
                    : "text-[#a7a7a7] hover:text-white"
                }`}
              >
                Quarterly
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-20 w-full">
          <button
            type="button"
            onClick={onToggle}
            className={`flex items-center justify-between px-[18px] py-[16px] w-full ${
              selected
                ? "bg-[#3d3d3d] text-white"
                : "bg-white text-black hover:bg-[#F3F3F3]"
            }`}
          >
            <span className="flex items-center gap-10">
              <span className="text-15 font-body">
                {actionLabel ?? (selected ? "Added to Stack" : "Add to Stack")}
              </span>
              {flatCents != null ? (
                <>
                  <span
                    className="inline-block w-[1px] h-[20px] bg-current opacity-30"
                    aria-hidden
                  />
                  <span className="flex items-end font-body">
                    {memberFlatCents != null && memberFlatCents !== flatCents && (
                      <span className="text-12 leading-[1.9] mr-5 line-through opacity-60">
                        {formatPrice(flatCents)}
                      </span>
                    )}
                    <span className="text-15 leading-normal">
                      {formatPrice(memberFlatCents)}
                    </span>
                    <span className="text-11 leading-[1.9] ml-3">
                      {isIntro ? periodSuffix : renewalPeriod}
                    </span>
                  </span>
                </>
              ) : null}
            </span>
            <span className="shrink-0 flex items-center justify-center">
              {selected ? (
                <CheckmarkIcon className="w-[1rem] h-[1rem]" />
              ) : (
                <ArrowIcon className="w-[1rem] h-auto" />
              )}
            </span>
          </button>

          {(isIntro && renewalCents != null) || discountPct ? (
            <div className="flex items-center justify-between w-full">
              {isIntro && renewalCents != null ? (
                <div className="font-body text-12 text-[#a8a8a8] leading-normal m-0">
                  {formatPrice(memberRenewalCents)} {renewalPeriod} after first month
                  {memberRenewalCents !== renewalCents ? " (member price)" : ""}
                </div>
              ) : (
                <span />
              )}
              {discountPct ? (
                <div className="bg-[#545d72] flex items-center gap-6 rounded-[.5rem] px-10 py-5">
                  <div className="w-[1.5rem] h-[1.5rem] flex items-center justify-center rounded-[9999px] overflow-hidden bg-[#DCE7FF]/50">
                    <img
                      src="/icons/instock.gif"
                      alt="animated circle"
                      className="w-full h-full object-cover mix-blend-lighten rounded-full"
                    />
                  </div>
                  <div className="font-body text-12 text-[#dce7ff] text-center whitespace-nowrap m-0">
                    {discountPct}% off your first{" "}
                    {isQuarterly ? "quarter" : "month"}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
